import { API_URLS, getJSONData, parseErrorMessage } from '@/lib/api/config';
import { getAuthTokenClient } from '@/utils/helpers';
import {
  TypeResponseError,
  TypeUserCoursesResponseSuccess,
  UpdatePlaybackHistoryRequestBody,
  TypePlaybackHistoryResponseSuccess,
  TypeUserSkillSetsResponseSuccess,
} from '@/lib/api/playbackService/types';

export async function getTokensForMediaAsset(playbackId: string) {
  const body = {
    token_set: {
      playback_id: playbackId,
    },
  };

  const config = {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthTokenClient(),
    },
  };

  const response = await fetch(
    `${API_URLS.PLAYBACK_SERVICE_PROXY}/token_sets`,
    config,
  );

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  return await getJSONData(response);
}

export async function createPlaybackHistory(mediaAssetId: string) {
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthTokenClient(),
    },
  };

  const response = await fetch(
    `${API_URLS.PLAYBACK_SERVICE_PROXY}/media_assets/${mediaAssetId}/playback_histories`,
    config,
  );

  const { ok } = response;

  if (!ok) {
    return {
      ok,
      errorMessage: await parseErrorMessage(response),
    } as TypeResponseError;
  }

  return {
    ok,
    data: await getJSONData(response),
  } as TypePlaybackHistoryResponseSuccess;
}

export async function updatePlaybackHistory(
  playbackHistoryId: string,
  head: number,
  completed_at?: Date,
) {
  const body = {
    playback_history: {
      head,
    },
  } as UpdatePlaybackHistoryRequestBody;

  if (completed_at) {
    body.playback_history.completed_at = completed_at.toISOString();
  }

  const config = {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthTokenClient(),
    },
  };

  const response = await fetch(
    `${API_URLS.PLAYBACK_SERVICE_PROXY}/playback_histories/${playbackHistoryId}`,
    config,
  );

  const { ok } = response;

  if (!ok) {
    return {
      ok,
      errorMessage: await parseErrorMessage(response),
    } as TypeResponseError;
  }

  return {
    ok,
    data: await getJSONData(response),
  } as TypePlaybackHistoryResponseSuccess;
}

export async function getUserCourses(authToken?: string) {
  const config = {
    method: 'GET',
    headers: {
      Authorization: authToken ? authToken : await getAuthTokenClient(),
    },
  };

  const response = await fetch(
    `${API_URLS.PLAYBACK_SERVICE}/user_courses`,
    config,
  );

  const { ok } = response;

  if (!ok) {
    return {
      ok,
      errorMessage: await parseErrorMessage(response),
    } as TypeResponseError;
  }

  return {
    ok,
    data: await getJSONData(response),
  } as TypeUserCoursesResponseSuccess;
}

export async function getUserSkillSets(authToken?: string) {
  const config = {
    method: 'GET',
    headers: {
      Authorization: authToken ? authToken : await getAuthTokenClient(),
    },
  };

  const response = await fetch(
    `${API_URLS.PLAYBACK_SERVICE}/user_skill_sets`,
    config,
  );

  const { ok } = response;

  if (!ok) {
    return {
      ok,
      errorMessage: await parseErrorMessage(response),
    } as TypeResponseError;
  }

  return {
    ok,
    data: await getJSONData(response),
  } as TypeUserSkillSetsResponseSuccess;
}
